import { useRef, useState } from 'react';

interface QueuedSelection {
  ids: string[];
  save: (ids: string[]) => Promise<unknown>;
  onError: (error: Error) => void;
}

interface UseQueuedSelectionArgs {
  savedIds: string[];
  save: QueuedSelection['save'];
  onError: QueuedSelection['onError'];
}

type SelectionUpdate = string[] | ((currentIds: string[]) => string[]);

/** Keeps checkbox edits visible while serializing saves and combining queued selections. */
export function useQueuedSelection({ savedIds, save, onError }: UseQueuedSelectionArgs) {
  const pendingSelection = useRef<QueuedSelection | null>(null);
  const saveIsRunning = useRef(false);
  const [requestedIds, setRequestedIds] = useState<string[] | null>(null);

  const savePendingSelection = () => {
    if (saveIsRunning.current || pendingSelection.current === null) return;
    const selectionToSave = pendingSelection.current;
    saveIsRunning.current = true;

    const finishSave = () => {
      saveIsRunning.current = false;
      if (pendingSelection.current !== selectionToSave) {
        savePendingSelection();
        return;
      }
      pendingSelection.current = null;
      setRequestedIds(null);
    };

    void Promise.resolve()
      .then(() => selectionToSave.save(selectionToSave.ids))
      .then(finishSave, (error: Error) => {
        selectionToSave.onError(error);
        finishSave();
      });
  };

  const setSelection = (update: SelectionUpdate) => {
    const currentIds = pendingSelection.current?.ids ?? savedIds;
    const ids = typeof update === 'function' ? update(currentIds) : update;
    pendingSelection.current = { ids, save, onError };
    setRequestedIds(ids);
    savePendingSelection();
  };

  return { selection: requestedIds ?? savedIds, setSelection };
}
